// Credit Store for AIMetaPic V2
// Centralized state management for credit information

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { userService } from '../services/userService';

import { AuthCore } from '../core/AuthCore';

export interface CreditInfo {
  balance: number;
  required: number;
  cost_per_file: number;
}

export interface CreditState {
  balance: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchCreditBalance: () => Promise<void>;
  calculateCreditInfo: (filesCount: number) => CreditInfo;
  deductCredits: (amount: number) => void;
  addCredits: (amount: number) => void;
  clearError: () => void;
  reset: () => void;
}

const INITIAL_STATE = {
  balance: 0,
  isLoading: false,
  error: null,
};

export const useCreditStore = create<CreditState>()(
  devtools(
    persist(
      (set, get) => ({
        ...INITIAL_STATE,

        fetchCreditBalance: async () => {
          const currentState = get();
          
          // Avoid redundant calls if already loading
          if (currentState.isLoading) {
            return;
          }
          
          set({ isLoading: true, error: null });
          
          try {
            // Use the new unified credit system via userService
            const balance = await userService.getCreditBalance();
            
            set({ 
              balance,
              isLoading: false, 
              error: null
            });
          } catch (error) {
            set({ 
              isLoading: false, 
              error: error instanceof Error ? error.message : 'Failed to fetch credit balance'
            });
          }
        },



        calculateCreditInfo: (filesCount: number): CreditInfo => {
          // Input validation
          if (!Number.isInteger(filesCount) || filesCount < 0) {
            throw new Error('Files count must be a non-negative integer');
          }
          
          const state = get();
          // Get cost per file from environment variable with fallback to 1
          const costPerFile = Number(import.meta.env.VITE_COST_PER_FILE) || 1;
          
          return {
            balance: state.balance,
            required: filesCount * costPerFile,
            cost_per_file: costPerFile
          };
        },

        deductCredits: (amount: number) => {
          // Input validation
          if (!Number.isFinite(amount) || amount < 0) {
            throw new Error('Deduct amount must be a non-negative number');
          }
          
          const state = get();
          const newBalance = Math.max(0, state.balance - amount);
          set({ 
            balance: newBalance
          });
        },

        addCredits: (amount: number) => {
          // Input validation
          if (!Number.isFinite(amount) || amount < 0) {
            throw new Error('Add amount must be a non-negative number');
          }
          
          const state = get();
          const newBalance = state.balance + amount;
          set({ 
            balance: newBalance
          });
        },

        clearError: () => {
          set({ error: null });
        },





        reset: () => {
          set(INITIAL_STATE);
        }
      }),
      {
        name: 'credit-store',
        partialize: (state) => ({
          balance: state.balance
        }),
      }
    ),
    {
      name: 'credit-store',
    }
  ));

// Selectors for better performance
export const selectCreditBalance = (state: CreditState) => state.balance;
export const selectIsLoading = (state: CreditState) => state.isLoading;
export const selectError = (state: CreditState) => state.error;
// Helper functions removed as they're no longer needed in simplified system

export default useCreditStore;